import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, SectionLabel, Segmented, Spinner } from "./ui";
import DataTable from "./DataTable";
import KeyResultModal from "./KeyResultModal";
import { Crosshair, GitBranch, FolderKanban, Target } from "lucide-react";

const QUADRANTS = {
  quick_win: { color: "#10B981" },
  major_bet: { color: "#2563EB" },
  fill_in: { color: "#F59E0B" },
  thankless: { color: "#EF4444" },
};

function AlignBar({ value, color, testid }) {
  return (
    <div className="flex items-center gap-2 w-32 shrink-0">
      <div className="h-1.5 rounded-full overflow-hidden flex-1" style={{ background: "var(--muted)" }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${value || 0}%`, backgroundColor: color }} />
      </div>
      <span className="text-[11px] text-text-secondary tabular-nums w-8 text-right" data-testid={testid}>{value ?? 0}%</span>
    </div>
  );
}

/**
 * Shared OKR surface. When `entityId` is given it is scoped to that entity (no
 * cross-entity leakage); otherwise it is the group-wide cross-entity summary.
 */
export default function OkrView({ entityId = null }) {
  const { t, lang, entities } = useApp();
  const navigate = useNavigate();
  const [objectives, setObjectives] = useState([]);
  const [krs, setKrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("objectives");
  const [krModal, setKrModal] = useState(null);

  const entColor = (id) => entities.find((e) => e.id === id)?.color || "#A1A1AA";
  const entName = (id) => entities.find((e) => e.id === id)?.name || (lang === "fr" ? "Groupe" : "Group");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/okr/board${entityId ? `?entity_id=${entityId}` : ""}`);
      setObjectives(data.objectives);
      setKrs(data.key_results);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const krsByQuadrant = (q) => krs.filter((k) => k.quadrant === q);
  const krsForObjective = (oid) => krs.filter((k) => k.objective_id === oid);
  const alignment = objectives
    .map((o) => ({ ...o, krs: krsForObjective(o.id).filter((k) => k.goal_count > 0) }))
    .filter((o) => o.krs.length > 0);

  const tabOptions = [
    { value: "objectives", label: t("objectives") },
    { value: "key_results", label: t("key_results") },
  ];
  const defaults = entityId ? { entity_id: entityId } : {};

  if (loading) return <div className="py-16 flex justify-center"><Spinner className="w-7 h-7 text-text-secondary" /></div>;

  const isEmpty = objectives.length === 0 && krs.length === 0;
  if (isEmpty) {
    return (
      <Card className="text-center py-12" data-testid="okr-empty">
        <Target className="w-7 h-7 mx-auto mb-3 text-text-secondary" />
        <p className="text-text-secondary text-sm mb-4">{t("okr_empty")}</p>
        <div className="max-w-2xl mx-auto text-left">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-medium">{t("nav_okr")}</h3>
            <Segmented options={tabOptions} value={tab} onChange={setTab} testid="okr-manage-tabs" />
          </div>
          <DataTable resource={tab} key={`${tab}-${entityId || "all"}`} entityId={entityId || undefined} defaultValues={defaults} />
        </div>
      </Card>
    );
  }

  return (
    <div data-testid="okr-view">
      {/* Impact / Effort matrix */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-5">
          <Crosshair className="w-4 h-4 text-text-secondary" />
          <h3 className="font-heading font-medium">{t("impact_effort")}</h3>
        </div>
        <div className="flex gap-4">
          <div className="flex flex-col justify-between items-center text-[11px] text-text-secondary uppercase tracking-wider py-2">
            <span>{t("high")}</span>
            <span className="-rotate-90 whitespace-nowrap font-semibold">{t("impact")}</span>
            <span>{t("low")}</span>
          </div>
          <div className="flex-1">
            <div className="grid grid-cols-2 grid-rows-2 gap-3" style={{ minHeight: 360 }}>
              {["quick_win", "major_bet", "fill_in", "thankless"].map((q) => {
                const cfg = QUADRANTS[q];
                const items = krsByQuadrant(q);
                return (
                  <div key={q} data-testid={`quadrant-${q}`} className="rounded-xl border p-3 flex flex-col"
                    style={{ borderColor: `${cfg.color}55`, background: `${cfg.color}0d` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.color }} />
                      <span className="text-xs font-semibold" style={{ color: cfg.color }}>{t(q)}</span>
                      <span className="text-[10px] text-text-secondary ml-auto">{items.length}</span>
                    </div>
                    <div className="space-y-1.5 overflow-y-auto">
                      {items.map((k) => (
                        <button key={k.id} onClick={() => setKrModal(k)} data-testid={`kr-chip-${k.id}`}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg bg-surface hover:bg-surface-hover text-xs text-left transition-colors">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: entColor(k.entity_id) }} />
                          <span className="truncate flex-1">{k.title}</span>
                          <span className="text-[10px] text-text-secondary tabular-nums shrink-0">{k.progress}%</span>
                        </button>
                      ))}
                      {items.length === 0 && <span className="text-[11px] text-text-secondary">—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between items-center text-[11px] text-text-secondary uppercase tracking-wider mt-2 px-1">
              <span>{t("low")}</span>
              <span className="font-semibold">{t("effort")}</span>
              <span>{t("high")}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Objectives with key results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {objectives.map((o) => {
          const list = krsForObjective(o.id);
          return (
            <Card key={o.id} data-testid={`objective-${o.id}`}>
              <div className="flex items-start justify-between gap-3 mb-1">
                <h4 className="font-heading font-medium leading-tight">{o.title}</h4>
                <span className="text-[11px] px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: `${entColor(o.entity_id)}22`, color: entColor(o.entity_id) }}>
                  {entName(o.entity_id)}
                </span>
              </div>
              {o.quarter && <SectionLabel>{o.quarter}</SectionLabel>}
              <div className="flex items-center gap-2 mt-2 mb-1">
                <span className="text-[11px] text-text-secondary">{t("progress")}</span>
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${o.progress}%`, backgroundColor: entColor(o.entity_id) }} />
                </div>
                <span className="text-[11px] tabular-nums text-text-secondary">{o.progress}%</span>
              </div>
              <div className="mt-4 space-y-4">
                {list.map((k) => (
                  <button key={k.id} onClick={() => setKrModal(k)} data-testid={`kr-${k.id}`} className="w-full text-left group">
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="truncate pr-2 group-hover:underline">{k.title}</span>
                      <span className="text-text-secondary tabular-nums shrink-0">
                        {k.goal_count > 0
                          ? <span className="inline-flex items-center gap-1">{k.goal_count} {t("goals_short")} · {k.progress}%</span>
                          : <span>{k.current}/{k.target} {k.unit}</span>}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${k.progress}%`, backgroundColor: QUADRANTS[k.quadrant]?.color || "#2563EB" }} />
                    </div>
                  </button>
                ))}
                {list.length === 0 && <p className="text-sm text-text-secondary">{t("no_data")}</p>}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Alignment tree */}
      {alignment.length > 0 && (
        <Card className="mb-8" data-testid="alignment-tree">
          <div className="flex items-center gap-2 mb-1">
            <GitBranch className="w-4 h-4 text-text-secondary" />
            <h3 className="font-heading font-medium">{t("alignment_title")}</h3>
          </div>
          <p className="text-text-secondary text-sm mb-5">{t("alignment_subtitle")}</p>
          <div className="space-y-6">
            {alignment.map((o) => (
              <div key={o.id} data-testid={`align-objective-${o.id}`}>
                <div className="flex items-center gap-3 mb-2.5">
                  <Target className="w-4 h-4 shrink-0" style={{ color: entColor(o.entity_id) }} />
                  <span className="font-medium" data-testid={`align-objective-title-${o.id}`}>{o.title}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: `${entColor(o.entity_id)}22`, color: entColor(o.entity_id) }}>{entName(o.entity_id)}</span>
                  <div className="ml-auto"><AlignBar value={o.progress} color={entColor(o.entity_id)} testid={`align-objective-progress-${o.id}`} /></div>
                </div>
                <div className="ml-2 pl-4 border-l space-y-3">
                  {o.krs.map((kr) => (
                    <div key={kr.id} data-testid={`align-kr-${kr.id}`}>
                      <button onClick={() => setKrModal(kr)} data-testid={`align-kr-btn-${kr.id}`}
                        className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-surface-hover transition-colors text-left">
                        <Crosshair className="w-3.5 h-3.5 shrink-0" style={{ color: QUADRANTS[kr.quadrant]?.color || "#2563EB" }} />
                        <span className="text-sm font-medium flex-1 truncate">{kr.title}</span>
                        <AlignBar value={kr.progress} color={QUADRANTS[kr.quadrant]?.color || "#2563EB"} testid={`align-kr-progress-${kr.id}`} />
                      </button>
                      <div className="ml-3 pl-4 border-l space-y-1 mt-1">
                        {kr.goals.map((g) => (
                          <div key={g.id} data-testid={`align-goal-${g.id}`}>
                            <button onClick={() => navigate(`/entity/${g.entity_key}/space/${g.space_id}/goal/${g.id}`)}
                              data-testid={`align-goal-btn-${g.id}`}
                              className="w-full flex items-center gap-2.5 px-2.5 py-1 rounded-lg hover:bg-surface-hover transition-colors text-left">
                              <Target className="w-3 h-3 shrink-0" style={{ color: g.entity_color }} />
                              <span className="text-sm flex-1 truncate">{g.name}</span>
                              <span className="text-[10px] text-text-secondary shrink-0">{g.entity_name}</span>
                              <AlignBar value={g.progress} color={g.entity_color} testid={`align-goal-progress-${g.id}`} />
                            </button>
                            {g.children.length > 0 && (
                              <div className="ml-3 pl-4 border-l space-y-1 mt-1">
                                {g.children.map((c) => (
                                  <button key={c.id} onClick={() => navigate(`/entity/${c.entity_key}/space/${c.space_id}/${c.kind === "project" ? "project" : "goal"}/${c.id}`)}
                                    data-testid={`align-child-${c.id}`}
                                    className="w-full flex items-center gap-2.5 px-2.5 py-1 rounded-lg hover:bg-surface-hover transition-colors text-left">
                                    {c.kind === "project" ? <FolderKanban className="w-3 h-3 shrink-0 text-text-secondary" /> : <Target className="w-3 h-3 shrink-0 text-text-secondary" />}
                                    <span className="text-xs text-text-secondary flex-1 truncate">{c.name}</span>
                                    <AlignBar value={c.progress} color={c.entity_color} testid={`align-child-progress-${c.id}`} />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Management */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-medium text-lg">{t("nav_okr")}</h3>
        <Segmented options={tabOptions} value={tab} onChange={setTab} testid="okr-manage-tabs" />
      </div>
      <DataTable resource={tab} key={`${tab}-${entityId || "all"}`} entityId={entityId || undefined} defaultValues={defaults} />

      <KeyResultModal open={!!krModal} onClose={() => setKrModal(null)} onSaved={load} kr={krModal} objectives={objectives} />
    </div>
  );
}
